import {
   Patient,
   PatientAlreadyExistsFault,
   PatientExternalIdentifierUpdateCommand,
} from '@dreryk/egabinet-patient-frontend-api';
import { PatientExternalIdentifier } from '@dreryk/egabinet-patient-published-language-api';
import { NationalIdentificationNumber, Passport, Pesel } from '@dreryk/personal-external-identifier-common-api';

import { useCqrsCommand } from '@dreryk/command-query-react-common';
import {
   dispatchSnackbarNegativeEvent,
   dispatchSnackbarPositiveEvent,
   ModalConfirmButton,
} from '@dreryk/react-components';

import { createPatientExternalIdentifierTypeVisitor } from '~libs.desktop.shared.fields';

import { translationKeys } from '../../../translations';

import {
   PatientExternalIdentifierTypeStillValidFormBean,
   PatientExternalIdentifierTypeStillValidFormController,
} from './PatientExternalIdentifierTypeStillValidForm';

type PatientExternalIdentifierTypeStillValidConfirmButtonProps = Pick<
   PatientExternalIdentifierTypeStillValidFormController,
   'handleSubmit'
> & {
   readonly patientId: number;
   readonly onSuccess: VoidFunction;
};

export const PatientExternalIdentifierTypeStillValidConfirmButton = (
   props: PatientExternalIdentifierTypeStillValidConfirmButtonProps
): JSX.Element => {
   const { executeAsync: updatePatientExternalIdentifier } = useCqrsCommand<
      Patient,
      PatientExternalIdentifierTypeStillValidFormBean,
      PatientAlreadyExistsFault
   >(
      (parameters) => {
         const command = new PatientExternalIdentifierUpdateCommand();
         const patientExternalIdentifierTypeVisitor = createPatientExternalIdentifierTypeVisitor(
            parameters.patientExternalIdentifierType
         );

         const patientExternalIdentifier = PatientExternalIdentifier.of(
            patientExternalIdentifierTypeVisitor<Pesel | Passport | NationalIdentificationNumber>({
               visitPesel: () => Pesel.of(parameters.patientExternalIdentifierValue!),
               visitPassport: () =>
                  Passport.of(
                     parameters.passportCountry!,
                     parameters.patientExternalIdentifierValue!,
                     parameters.passportValidTo!
                  ),
               visitNationalIdentificationNumber: () =>
                  NationalIdentificationNumber.of(
                     parameters.nationalIdentificationNumberCountry!,
                     parameters.patientExternalIdentifierValue!
                  ),
            })
         );

         command.patientId = props.patientId;
         command.patientExternalIdentifier = patientExternalIdentifier;
         return command;
      },
      {
         onSuccess: () => {
            dispatchSnackbarPositiveEvent(translationKeys.patientDataHasBeenUpdated);
            props.onSuccess();
         },
         onFault: {
            'egabinet-patient-frontend-api:PatientAlreadyExistsFault': () => {
               dispatchSnackbarNegativeEvent(translationKeys.patientWithGivenPeselAlreadyExists);
            },
         },
      }
   );

   return (
      <ModalConfirmButton label={translationKeys.save} onClick={props.handleSubmit(updatePatientExternalIdentifier)} />
   );
};
